import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import WhatsAppButton from "../../components/feature/WhatsAppButton";

const services = [
  "Medical & Clinic Cleaning",
  "Corporate Office Cleaning",
  "Bank & Financial Institution",
  "Car Dealership Cleaning",
  "Airbnb & Short-Term Rental",
  "Retail & Commercial Space",
  "Other / Not Listed",
];

const frequencies = [
  "One-Time",
  "Weekly",
  "Bi-Weekly",
  "Monthly",
  "Custom Schedule",
];

const whyUs = [
  { icon: "ri-shield-check-line", label: "Fully Insured & Licensed" },
  { icon: "ri-timer-line", label: "Fast Response Within 24hrs" },
  { icon: "ri-leaf-line", label: "Eco-Friendly Products" },
  { icon: "ri-thumb-up-line", label: "100% Satisfaction Guarantee" },
  { icon: "ri-user-check-line", label: "Background-Checked Team" },
  { icon: "ri-award-line", label: "10+ Years of Experience" },
];

const PricingPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((value, key) => {
      body.append(key, value.toString());
    });
    setLoading(true);
    try {
      await fetch("https://readdy.ai/api/form/d74olin5hic0eqh319mg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ fontFamily: "'Poppins', sans-serif" }}>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-[#0F2A44] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20geometric%20diagonal%20lines%20and%20gold%20accents%20on%20deep%20navy%20blue%20background%2C%20minimalist%20premium%20luxury%20design%20texture%2C%20high%20resolution%20professional%20backdrop&width=1920&height=600&seq=quotebg001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mb-4 block">
            Free Estimate
          </span>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-5">
            Get Your Custom Quote
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Every facility is different. Tell us about yours and we'll get back to you with a tailored cleaning proposal — no obligations.
          </p>
          <div className="flex justify-center mt-8 text-sm text-white/50">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors duration-200">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-[#D4AF37]">Get a Quote</span>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-[#F9F9F9]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">

            {/* Left — Info Panel */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-[#0F2A44] mb-3">
                  Why Choose Prime Shine?
                </h2>
                <p className="text-gray-500 text-sm leading-relaxed">
                  We provide professional-grade commercial cleaning for businesses across the greater Seattle and Washington area. No guesswork — we assess your space and deliver a precise, transparent quote.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {whyUs.map((item) => (
                  <div key={item.label} className="flex items-center gap-3 bg-white rounded-xl px-5 py-4 border border-gray-100">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#D4AF37]/15 shrink-0">
                      <i className={`${item.icon} text-base text-[#D4AF37]`} />
                    </div>
                    <span className="text-sm font-medium text-[#0F2A44]">{item.label}</span>
                  </div>
                ))}
              </div>

              {/* Contact Shortcuts */}
              <div className="bg-[#0F2A44] rounded-2xl p-6 space-y-4">
                <p className="text-white/80 text-sm font-medium">Prefer to reach us directly?</p>
                <a
                  href="tel:+12064744668"
                  className="flex items-center gap-3 text-white hover:text-[#D4AF37] transition-colors duration-200 cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10">
                    <i className="ri-phone-line text-sm" />
                  </div>
                  <span className="text-sm font-semibold">+1 (206) 474-4668</span>
                </a>
                <a
                  href="sms:+12064744668"
                  className="flex items-center gap-3 text-white hover:text-[#D4AF37] transition-colors duration-200 cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10">
                    <i className="ri-message-line text-sm" />
                  </div>
                  <span className="text-sm font-semibold">Text Us</span>
                </a>
                <a
                  href="mailto:info@primeshinewa.com"
                  className="flex items-center gap-3 text-white hover:text-[#D4AF37] transition-colors duration-200 cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10">
                    <i className="ri-mail-line text-sm" />
                  </div>
                  <span className="text-sm font-semibold">info@primeshinewa.com</span>
                </a>
              </div>
            </div>

            {/* Right — Form */}
            <div className="lg:col-span-3 bg-white rounded-2xl border border-gray-100 p-8 md:p-10">
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D4AF37]/15 mb-5">
                    <i className="ri-check-double-line text-3xl text-[#D4AF37]" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#0F2A44] mb-3">Quote Request Sent!</h3>
                  <p className="text-gray-500 text-sm max-w-sm">
                    Thank you! Our team will review your request and reach out within 24 hours with a custom proposal for your facility.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-8 text-sm font-semibold text-[#D4AF37] underline underline-offset-4 cursor-pointer"
                  >
                    Submit another request
                  </button>
                </div>
              ) : (
                <>
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-[#0F2A44] mb-1">Request a Free Quote</h2>
                    <p className="text-gray-400 text-sm">Fill in the details below and we'll prepare your estimate.</p>
                  </div>

                  <form
                    data-readdy-form
                    id="quote-request-form"
                    onSubmit={handleSubmit}
                    className="space-y-5"
                  >
                    {/* Name + Company */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-[#0F2A44] mb-1.5 uppercase tracking-wide">
                          Full Name <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          name="full_name"
                          required
                          placeholder="Jane Smith"
                          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 placeholder-gray-300 focus:outline-none focus:border-[#D4AF37] transition-colors duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#0F2A44] mb-1.5 uppercase tracking-wide">
                          Company / Business
                        </label>
                        <input
                          type="text"
                          name="company"
                          placeholder="Acme Corp"
                          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 placeholder-gray-300 focus:outline-none focus:border-[#D4AF37] transition-colors duration-200"
                        />
                      </div>
                    </div>

                    {/* Email + Phone */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-[#0F2A44] mb-1.5 uppercase tracking-wide">
                          Email <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          placeholder="you@company.com"
                          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 placeholder-gray-300 focus:outline-none focus:border-[#D4AF37] transition-colors duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#0F2A44] mb-1.5 uppercase tracking-wide">
                          Phone Number <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          placeholder="(206) 000-0000"
                          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 placeholder-gray-300 focus:outline-none focus:border-[#D4AF37] transition-colors duration-200"
                        />
                      </div>
                    </div>

                    {/* Service Type */}
                    <div>
                      <label className="block text-xs font-semibold text-[#0F2A44] mb-1.5 uppercase tracking-wide">
                        Type of Facility <span className="text-red-400">*</span>
                      </label>
                      <select
                        name="service_type"
                        required
                        defaultValue=""
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#D4AF37] transition-colors duration-200 cursor-pointer appearance-none bg-white"
                      >
                        <option value="" disabled>Select a service type...</option>
                        {services.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>

                    {/* Frequency + Square Footage */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-[#0F2A44] mb-1.5 uppercase tracking-wide">
                          Cleaning Frequency <span className="text-red-400">*</span>
                        </label>
                        <select
                          name="frequency"
                          required
                          defaultValue=""
                          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#D4AF37] transition-colors duration-200 cursor-pointer appearance-none bg-white"
                        >
                          <option value="" disabled>Select frequency...</option>
                          {frequencies.map((f) => (
                            <option key={f} value={f}>{f}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#0F2A44] mb-1.5 uppercase tracking-wide">
                          Approx. Square Footage
                        </label>
                        <input
                          type="text"
                          name="square_footage"
                          placeholder="e.g. 2,500 sq ft"
                          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 placeholder-gray-300 focus:outline-none focus:border-[#D4AF37] transition-colors duration-200"
                        />
                      </div>
                    </div>

                    {/* Location */}
                    <div>
                      <label className="block text-xs font-semibold text-[#0F2A44] mb-1.5 uppercase tracking-wide">
                        Facility Address / City
                      </label>
                      <input
                        type="text"
                        name="location"
                        placeholder="Seattle, WA"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 placeholder-gray-300 focus:outline-none focus:border-[#D4AF37] transition-colors duration-200"
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-xs font-semibold text-[#0F2A44] mb-1.5 uppercase tracking-wide">
                        Additional Details
                      </label>
                      <textarea
                        name="message"
                        rows={4}
                        maxLength={500}
                        placeholder="Describe any specific cleaning requirements, access instructions, or questions..."
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 placeholder-gray-300 focus:outline-none focus:border-[#D4AF37] transition-colors duration-200 resize-none"
                        onChange={(e) => setCharCount(e.target.value.length)}
                      />
                      <p className={`text-right text-xs mt-1 ${charCount >= 500 ? "text-red-400 font-semibold" : "text-gray-300"}`}>
                        {charCount}/500
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={loading || charCount > 500}
                      className="w-full bg-[#D4AF37] hover:bg-[#B8952E] disabled:opacity-60 text-[#0F2A44] font-bold py-4 rounded-full text-sm transition-all duration-200 hover:scale-[1.02] whitespace-nowrap cursor-pointer"
                    >
                      {loading ? "Sending Request..." : "Request My Free Quote"}
                    </button>

                    <p className="text-center text-xs text-gray-400">
                      We respond within 24 hours. No commitment required.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Bottom Trust Bar */}
      <section className="py-14 bg-white border-t border-gray-100">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-[#0F2A44] font-bold text-lg mb-8">Trusted by businesses across Washington State</p>
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-12">
            {[
              { icon: "ri-hospital-line", label: "Medical Clinics" },
              { icon: "ri-building-4-line", label: "Corporate Offices" },
              { icon: "ri-bank-line", label: "Banks & Financial" },
              { icon: "ri-car-line", label: "Car Dealerships" },
              { icon: "ri-store-line", label: "Retail Spaces" },
            ].map((item) => (
              <div key={item.label} className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D4AF37]/12">
                  <i className={`${item.icon} text-lg text-[#D4AF37]`} />
                </div>
                <span className="text-xs text-gray-400 font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default PricingPage;
